<template>
  <div>
    <w-sliding-popup ref="commonEventHistPopup" :type="popupType" :title="popupTitle" width="800px" @close="onClose">
      <div slot="content">
        <el-row type="flex" justify="end" class="title-bar vertical-center-box">
          <w-smart-analyzer-button v-if="eventType === 'EVENT'" class="mr-r4" activeTab="event-hist"></w-smart-analyzer-button>
          <el-button v-if="eventType === 'PREDICT_EVENT'" type="secondary" :disabled="!isAbleConfirmBtn" class="mr-r4" size="small" @click="openRscSmartAnalyzerPopup">Smart Analyzer</el-button>
          <w-recommend-handling-trouble-button v-if="eventType === 'PREDICT_EVENT'" class="mr-r4" :disabled-flag="isAbleConfirmBtn" :event-hist-seq-numbers="eventHistSeqNumbers" :is-predict-event="true"></w-recommend-handling-trouble-button>
          <w-header-search-button size="mini" @click="showSearchTool = !showSearchTool"></w-header-search-button>
        </el-row>
        <el-row>
          <el-table-wrapper style="width:100%;" ref="eventTable" border :data="tableData" :columns="tableColumns" pagination :column-default="columnDefault"
            :show-custom-header="true" :show-search-tool="showSearchTool" @selection-change="changeHandleSelection">
            <template slot-scope="scope" slot="eventlevel-slot">
              <w-eventlevel-cell :eventlevel-id="scope.row.eventlevelId"/>
            </template>
            <template slot-scope="scope" slot="event-message-slot">
              <div v-if="!isNil(scope.row.dynamicThresholdFlag) && scope.row.dynamicThresholdFlag === 'Y'" class="round-box-info-text red">
                동적
              </div>
              <span class="link c-pointer" @click="clickEventMessage(scope.row)">{{scope.row.eventMessage}}</span>
            </template>
          </el-table-wrapper>
        </el-row>
      </div>
    </w-sliding-popup>
    <w-resource-smart-analyzer-popup />
  </div>
</template>

<script>
import { isNil } from 'lodash';
import { columnDefault } from '@/constants/table';
import { EMPTY_TEXT } from '@/utils/string';
import { getDateToString } from '@/utils/date';
import { openFaultAnalysisPopup, closeFaultAnalysisPopup, isShowFaultAnalysisPopup } from '@/utils/event';
import pushNotificationApi from '@/api/frame/push-notification';
import WEventlevelCell from '@/components/table/EventlevelCell';
import WResourceSmartAnalyzerPopup from '@/components/popup/ResourceSmartAnalyzerPopup';
import WSmartAnalyzerButton from '@/views/event/components/SmartAnalyzerButton';
import WRecommendHandlingTroubleButton from '@/views/event/components/RecommendHandlingTroubleButton';

const dateFormatter = (row, column, value) => (isNil(value) ? EMPTY_TEXT : getDateToString(value));
const eventHistColumns = [
  {
    type: 'selection',
    width: 47,
  },
  {
    prop: 'eventlevelId',
    label: '등급',
    width: 75,
    scopedSlot: 'eventlevel-slot',
    searchable: true,
    searchType: 'eventlevel',
  },
  {
    prop: 'objName',
    label: '장비명',
    width: 110,
    sortable: true,
    searchable: true,
  },
  {
    prop: 'rsctypeName',
    label: '자원 구분',
    width: 100,
    sortable: true,
    searchable: true,
  },
  {
    prop: 'eventMessage',
    label: '장애 메세지',
    scopedSlot: 'event-message-slot',
    sortable: true,
    searchable: true,
  },
  {
    prop: 'checkDatetime',
    label: '발생시각',
    width: 130,
    sortable: true,
    searchable: true,
    searchType: 'date',
    formatter: dateFormatter,
  },
];
const predictEventHistColumns = [
  {
    type: 'selection',
    width: 47,
  },
  {
    prop: 'eventlevelId',
    label: '등급',
    width: 75,
    scopedSlot: 'eventlevel-slot',
    searchable: true,
    searchType: 'eventlevel',
  },
  {
    prop: 'objName',
    label: '장비명',
    width: 110,
    sortable: true,
    searchable: true,
  },
  {
    prop: 'rsctypeName',
    label: '자원 구분',
    width: 100,
    sortable: true,
    searchable: true,
  },
  {
    prop: 'eventMessage',
    label: '장애 메세지',
    scopedSlot: 'event-message-slot',
    sortable: true,
    searchable: true,
  },
  {
    prop: 'checkDatetime',
    label: '예상 발생시각',
    width: 130,
    sortable: true,
    searchable: true,
    searchType: 'date',
    formatter: dateFormatter,
  },
];

export default {
  name: 'WCommonEventHistPopup',
  components: {
    WEventlevelCell,
    WResourceSmartAnalyzerPopup,
    WSmartAnalyzerButton,
    WRecommendHandlingTroubleButton,
  },
  data() {
    return {
      columnDefault,
      showSearchTool: false,
      selectedRows: [],
      tableData: [],
      tableColumns: [],
      popupType: 'common', // default
    };
  },
  computed: {
    visible() {
      return this.$store.state.popup.showCommonEventHistPopup;
    },
    eventHistInfo() {
      return this.$store.state.popup.commonEventHistPopupParams;
    },
    popupTitle() {
      return this.eventType === 'EVENT' ? '이벤트 발생 현황' : '사전 예측 이벤트';
    },
    eventType() {
      return this.eventHistInfo.type;
    },
    isAbleConfirmBtn() {
      return this.selectedRows.length > 0;
    },
    eventHistSeqNumbers() {
      return this.selectedRows.map((item) => item.seqNumber);
    },
    showSettingView() {
      return this.$store.state.common.showSettingView;
    },
  },
  watch: {
    visible: {
      handler(value) {
        if (value) {
          this.open();
        } else {
          this.close();
        }
      },
    },
  },
  beforeDestroy() {
    if (this.visible) {
      this.close();
    }
  },
  methods: {
    isNil,
    open() {
      this.$refs.commonEventHistPopup.open();
      this.popupType = this.showSettingView ? 'setting' : 'common';
      this.tableColumns = [];
      this.$nextTick(() => {
        this.tableColumns = this.eventType === 'EVENT' ? eventHistColumns : predictEventHistColumns;
        this.loadData();
      });
    },
    close() {
      this.$refs.commonEventHistPopup.close();
    },
    onClose() {
      this.showSearchTool = false;
      this.$refs.eventTable.clearFilter();
      this.$store.dispatch('closeCommonEventHistPopup');
    },
    /**
     * 이벤트 발생 현황 / 사전 예측 이벤트 목록 조회
     * @author jeee12
     */
    loadData() {
      const params = {
        startSeqNumber: this.eventHistInfo.startSeqNumber,
        endSeqNumber: this.eventHistInfo.endSeqNumber,
      };
      if (this.eventType === 'EVENT') {
        pushNotificationApi.getPushNotificationEventInfo(params).then((response) => {
          if (response.data.success) {
            this.tableData = response.data.data;
          }
        });
      } else {
        pushNotificationApi.getPushNotificationPredictEventInfo(params).then((response) => {
          if (response.data.success) {
            this.tableData = response.data.data;
          }
        });
      }
    },
    /**
     * 테이블 selection change
     * @author jeee12
     * @param  {[type]} selectVal [description]
     */
    changeHandleSelection(selectVal) {
      if (this.eventType === 'EVENT') {
        this.$store.dispatch('setSelectedEvents', selectVal);
      } else {
        this.selectedRows = selectVal;
      }
    },
    /**
     * 이벤트 메세지 클릭 (성능/운영 > 분석으로 라우터 이동)
     * @author jeee12
     * @param  {[type]} row [description]
     */
    clickEventMessage(row) {
      if (!isNil(row.seqNumber)) {
        if (isShowFaultAnalysisPopup()) {
          closeFaultAnalysisPopup();
          this.$nextTick(() => {
            openFaultAnalysisPopup(row.seqNumber);
          });
        } else {
          openFaultAnalysisPopup(row.seqNumber);
        }
      }
    },
    /**
     * 사전 예측 이벤트에 대한 Smart Analyzer 팝업 open
     * @author jeee12
     * @return {[type]} [description]
     */
    openRscSmartAnalyzerPopup() {
      const objRscKeyList = this.selectedRows.map((item) => ({
        objId: item.objId,
        managetypeId: item.managetypeId,
        rsctypeId: item.rsctypeId,
        rscId: item.rscId,
        rscKey: item.rscKey,
      }));
      this.$store.dispatch('openResourceSmartAnalyzerPopup', objRscKeyList);
    },
  },
};
</script>

<style>

</style>



// WEBPACK FOOTER //
// src/components/popup/CommonEventHistPopup/index.vue